import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import type {
  AdminSignaturesJournalDto,
  AdminSignedContactsDto,
  AdminSignedSiretsDto,
  AdminSignedStatusBySiretDto,
  CguRegistreVersionDetailDto,
  CguRegistreVersionRowDto,
  CreateCguDraftInput,
  EditCguDraftInput,
  ForcedResignatureDto,
  LegalAdminClient,
} from '@/api/legal-admin-client';
import { useLegalAdminClient } from '@/api/legal-admin-client-context';
import { suppliersListKey, supplierDetailKey } from '@/api/use-suppliers';

// =============================================================================
// use-legal-admin.ts — Hooks TanStack Query de l'ADMIN CGU (S5bis + A-CGU ÉTAPE 2).
// =============================================================================
// LECTURES cross-fournisseur (withAdmin) : statut par SIRET, journal §2.9, signataires.
// REGISTRE des versions : liste, count, détail. WRITES : create draft / publish / delete
// (useMutation avec invalidation du registre + count + journal). ZÉRO règle métier ici :
// l'autorité (RLS, transitions d'état, garde Brouillon-only) est l'API ; l'erreur remonte
// telle quelle. La COMPOSITION (merge colonne, anti-join) se fait DANS LES ÉCRANS.

const LEGAL_ADMIN_KEY = ['legal-admin', 'cgu'] as const;

/** Clé de query du statut de signature par SIRET (colonne A-03/A-04). */
export const cguSignedStatusKey = [...LEGAL_ADMIN_KEY, 'signed-status'] as const;
/** Clé de query du journal §2.9. */
export const cguSignaturesJournalKey = [...LEGAL_ADMIN_KEY, 'signatures'] as const;
/** Clé de query du set des signataires de la version courante. */
export const cguSignedSiretsKey = [...LEGAL_ADMIN_KEY, 'signed-sirets'] as const;
/** Clé de query des signataires de la version courante au grain CONTACT (FIX-FOU-2 / DEC-48). */
export const cguSignedContactsKey = [...LEGAL_ADMIN_KEY, 'signed-contacts'] as const;
/** Clé de query du registre des versions. */
export const cguVersionsKey = [...LEGAL_ADMIN_KEY, 'versions'] as const;
/** Clé de query du compteur de versions. */
export const cguVersionsCountKey = [...LEGAL_ADMIN_KEY, 'versions', 'count'] as const;
/** Clé de query du détail d'une version. */
export const cguVersionKey = (id: string): readonly unknown[] => [
  ...LEGAL_ADMIN_KEY,
  'versions',
  'detail',
  id,
];
/** Clé de query du journal des re-signatures forcées d'un SIRET (R15-GOV-04). */
export const cguForcedResignaturesKey = (siret: string): readonly unknown[] => [
  ...LEGAL_ADMIN_KEY,
  'forced-resignatures',
  siret,
];

/** Map siret → statut de signature (colonne « Signature CGU » de A-03/A-04). */
export function useCguSignedStatus(): UseQueryResult<
  AdminSignedStatusBySiretDto,
  Error
> {
  const client: LegalAdminClient = useLegalAdminClient();
  return useQuery<AdminSignedStatusBySiretDto, Error>({
    queryKey: cguSignedStatusKey,
    queryFn: () => client.signedStatusBySiret(),
  });
}

/** Journal §2.9 — registre des signatures cross-fournisseur (IP masquée à la source). */
export function useCguSignaturesJournal(): UseQueryResult<
  AdminSignaturesJournalDto,
  Error
> {
  const client: LegalAdminClient = useLegalAdminClient();
  return useQuery<AdminSignaturesJournalDto, Error>({
    queryKey: cguSignaturesJournalKey,
    queryFn: () => client.signaturesJournal(),
  });
}

/** Set des signataires de la version courante (anti-join non-signataires côté écran). */
export function useCguSignedSirets(): UseQueryResult<AdminSignedSiretsDto, Error> {
  const client: LegalAdminClient = useLegalAdminClient();
  return useQuery<AdminSignedSiretsDto, Error>({
    queryKey: cguSignedSiretsKey,
    queryFn: () => client.signedSiretsForCurrentVersion(),
  });
}

/**
 * Signataires de la version courante au grain CONTACT (FIX-FOU-2 / DEC-48). L'anti-join ALL de la
 * vue non-signataires (§2.9) croise `bySiret` avec les contacts Actifs (fan-out supplier-reference,
 * composition front) : un SIRET est listé non-signataire dès qu'≥1 de ses contacts Actifs n'a pas signé.
 */
export function useCguSignedContacts(): UseQueryResult<AdminSignedContactsDto, Error> {
  const client: LegalAdminClient = useLegalAdminClient();
  return useQuery<AdminSignedContactsDto, Error>({
    queryKey: cguSignedContactsKey,
    queryFn: () => client.signedContactsForCurrentVersion(),
  });
}

/** Registre des versions CGU (7 colonnes gelées + nbSignatures). */
export function useCguVersions(): UseQueryResult<
  readonly CguRegistreVersionRowDto[],
  Error
> {
  const client: LegalAdminClient = useLegalAdminClient();
  return useQuery<readonly CguRegistreVersionRowDto[], Error>({
    queryKey: cguVersionsKey,
    queryFn: () => client.listVersions(),
  });
}

/** Compteur de versions au registre (DBU). */
export function useCguVersionsCount(): UseQueryResult<
  { readonly count: number },
  Error
> {
  const client: LegalAdminClient = useLegalAdminClient();
  return useQuery<{ readonly count: number }, Error>({
    queryKey: cguVersionsCountKey,
    queryFn: () => client.countVersions(),
  });
}

/** Détail d'une version (contenu HTML + métadonnées). Gaté par `id` (enabled). */
export function useCguVersion(
  id: string | undefined,
): UseQueryResult<CguRegistreVersionDetailDto, Error> {
  const client: LegalAdminClient = useLegalAdminClient();
  return useQuery<CguRegistreVersionDetailDto, Error>({
    queryKey: cguVersionKey(id ?? ''),
    queryFn: () => client.getVersion(id as string),
    enabled: id !== undefined && id.length > 0,
  });
}

/**
 * Invalide les lectures du registre (liste + count + journal). Après tout write, le registre
 * doit refléter le nouvel état (nbSignatures, états, existence). Regroupé pour parité.
 */
function useInvalidateRegistre(): () => Promise<void> {
  const queryClient = useQueryClient();
  return async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: cguVersionsKey }),
      queryClient.invalidateQueries({ queryKey: cguVersionsCountKey }),
      queryClient.invalidateQueries({ queryKey: cguSignaturesJournalKey }),
      queryClient.invalidateQueries({ queryKey: cguSignedSiretsKey }),
      queryClient.invalidateQueries({ queryKey: cguSignedContactsKey }),
      queryClient.invalidateQueries({ queryKey: cguSignedStatusKey }),
    ]);
  };
}

/** Création d'un BROUILLON (métadonnées + corps HTML, persisté et nettoyé côté back). */
export function useSaveCguDraft(): UseMutationResult<
  { readonly cguVersionId: string },
  Error,
  CreateCguDraftInput
> {
  const client: LegalAdminClient = useLegalAdminClient();
  const invalidate = useInvalidateRegistre();
  return useMutation<{ readonly cguVersionId: string }, Error, CreateCguDraftInput>({
    mutationKey: [...cguVersionsKey, 'save-draft'],
    mutationFn: (input: CreateCguDraftInput) => client.saveDraft(input),
    onSuccess: () => invalidate(),
  });
}

/** Variables d'édition d'un BROUILLON (id + patch partiel contentHtml/effectiveDate). */
export interface EditCguDraftVars {
  readonly cguVersionId: string;
  readonly input: EditCguDraftInput;
}

/**
 * Édition d'un BROUILLON (PATCH contenu). Après succès : invalide le DÉTAIL de la version (le corps
 * édité doit se recharger) PUIS le registre (lastModifiedAt). 404/409/400 remontent tels quels
 * (LegalAdminApiError porte le status ; l'écran mappe via `cguWriteErrorMessage`).
 */
export function useEditCguDraft(): UseMutationResult<
  { readonly cguVersionId: string; readonly state: string },
  Error,
  EditCguDraftVars
> {
  const client: LegalAdminClient = useLegalAdminClient();
  const queryClient = useQueryClient();
  const invalidate = useInvalidateRegistre();
  return useMutation<
    { readonly cguVersionId: string; readonly state: string },
    Error,
    EditCguDraftVars
  >({
    mutationKey: [...cguVersionsKey, 'edit-draft'],
    mutationFn: ({ cguVersionId, input }: EditCguDraftVars) =>
      client.editDraft(cguVersionId, input),
    onSuccess: async (_result, { cguVersionId }) => {
      await queryClient.invalidateQueries({ queryKey: cguVersionKey(cguVersionId) });
      await invalidate();
    },
  });
}

/** Publication d'un BROUILLON (archive la version courante, PDF write-once). */
export function usePublishCguVersion(): UseMutationResult<
  { readonly state: string },
  Error,
  string
> {
  const client: LegalAdminClient = useLegalAdminClient();
  const invalidate = useInvalidateRegistre();
  return useMutation<{ readonly state: string }, Error, string>({
    mutationKey: [...cguVersionsKey, 'publish'],
    mutationFn: (cguVersionId: string) => client.publishVersion(cguVersionId),
    onSuccess: () => invalidate(),
  });
}

/** Suppression d'un BROUILLON (Brouillon only ; 409 sinon, message serveur relayé). */
export function useDeleteCguDraft(): UseMutationResult<void, Error, string> {
  const client: LegalAdminClient = useLegalAdminClient();
  const invalidate = useInvalidateRegistre();
  return useMutation<void, Error, string>({
    mutationKey: [...cguVersionsKey, 'delete-draft'],
    mutationFn: (cguVersionId: string) => client.deleteDraft(cguVersionId),
    onSuccess: () => invalidate(),
  });
}

/**
 * Relance de signature CGU d'un fournisseur NON-signataire (T5 — REF 1.7 §5). Mutation par SIRET.
 * BEST-EFFORT : l'API publie l'intention (Domain Event) → worker email ; rien ne change de manière
 * synchrone dans le statut de signature (l'acceptation reste l'acte du fournisseur), donc AUCUNE
 * invalidation de query n'est déclenchée. L'erreur (409 déjà-signataire) remonte telle quelle à l'écran.
 */
export function useRelanceCgu(): UseMutationResult<void, Error, string> {
  const client: LegalAdminClient = useLegalAdminClient();
  return useMutation<void, Error, string>({
    mutationKey: [...LEGAL_ADMIN_KEY, 'relance'],
    mutationFn: (siret: string) => client.relanceCgu(siret),
  });
}

/** Variables de force-resignature (siret + motif OBLIGATOIRE). Le motif (PII) ne transite que dans le corps POST. */
export interface ForceResignatureVars {
  readonly siret: string;
  readonly motif: string;
}

/**
 * R15-GOV-04 — FORCE la resignature CGU ciblée d'un fournisseur (action admin, DBU §2.9 l.2136-2144).
 * Motif OBLIGATOIRE (re-validé serveur : 400 si absent/vide). Effet serveur : les contacts Actifs du
 * SIRET passent en ResignatureRequise (supplier-reference), le statut de signature LEGAL (`hasSigned`)
 * reste INCHANGÉ. On invalide donc les lectures FOURNISSEURS (liste A-03 + fiche A-04 du SIRET) pour que
 * l'état ResignatureRequise des contacts s'affiche ; les lectures LEGAL (signed-status) ne bougent pas.
 * L'erreur (400 motif/SIRET invalide) remonte telle quelle à l'écran.
 */
export function useForceResignatureCgu(): UseMutationResult<
  void,
  Error,
  ForceResignatureVars
> {
  const client: LegalAdminClient = useLegalAdminClient();
  const queryClient = useQueryClient();
  return useMutation<void, Error, ForceResignatureVars>({
    mutationKey: [...LEGAL_ADMIN_KEY, 'force-resignature'],
    mutationFn: ({ siret, motif }: ForceResignatureVars) =>
      client.forceResignature(siret, motif),
    onSuccess: async (_result, { siret }) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: suppliersListKey }),
        queryClient.invalidateQueries({ queryKey: supplierDetailKey(siret) }),
        // Le JOURNAL des re-signatures forcées du SIRET doit se recharger (nouvelle entrée journalisée).
        queryClient.invalidateQueries({
          queryKey: cguForcedResignaturesKey(siret),
        }),
      ]);
    },
  });
}

/**
 * R15-GOV-04 — JOURNAL des re-signatures forcées d'un fournisseur (DBU §2.9 l.2136-2144). Lecture
 * admin-relisible : le `motif` est restitué EN CLAIR (surface admin gardée). Gaté par `siret`
 * (enabled) ; la query est invalidée après un `useForceResignatureCgu` réussi (le journal se recharge).
 */
export function useForcedResignatures(
  siret: string | undefined,
): UseQueryResult<readonly ForcedResignatureDto[], Error> {
  const client: LegalAdminClient = useLegalAdminClient();
  return useQuery<readonly ForcedResignatureDto[], Error>({
    queryKey: cguForcedResignaturesKey(siret ?? ''),
    queryFn: () => client.listForcedResignatures(siret as string),
    enabled: siret !== undefined && siret.length > 0,
  });
}

/**
 * Téléchargement imperatif du PDF d'une version (Publiée/Archivée). Renvoie une mutation :
 * l'utilisateur clique → octets → download navigateur. 404 (Brouillon) → erreur relayée.
 */
export function useDownloadCguVersionPdf(): UseMutationResult<
  ArrayBuffer,
  Error,
  string
> {
  const client: LegalAdminClient = useLegalAdminClient();
  return useMutation<ArrayBuffer, Error, string>({
    mutationKey: [...cguVersionsKey, 'pdf'],
    mutationFn: (cguVersionId: string) => client.getVersionPdf(cguVersionId),
  });
}
